import * as React from 'react'
import { ImagePlus, Loader2 } from 'lucide-react'
import { FormError } from '@/components/ui/form-error'

/** The picker's types, and what a drop is held to as well: a dropped file skips the picker's filter. */
const ACCEPTED_TYPES: readonly string[] = ['image/png', 'image/jpeg', 'image/webp', 'image/gif']
const ACCEPT = ACCEPTED_TYPES.join(',')

/**
 * The largest file read. Every caller loads the whole file into the browser and
 * draws it onto a canvas to shrink it before anything is sent, so this is about
 * the page staying responsive, not the upload: a camera's largest JPEG fits
 * under it with room to spare.
 */
const MAX_SOURCE_MB = 25
const MAX_SOURCE_BYTES = MAX_SOURCE_MB * 1024 * 1024

/** Why some of the files given were left out, or null when none were. */
function refusalFor(wrongType: number, tooLarge: number): string | null {
  if (wrongType > 0 && tooLarge > 0) {
    return `Some files were left out: only PNG, JPEG, WebP and GIF photographs up to ${String(MAX_SOURCE_MB)} MB can be added.`
  }
  if (wrongType > 0) {
    return `${wrongType === 1 ? 'That file was' : `${String(wrongType)} files were`} left out: only PNG, JPEG, WebP and GIF photographs can be added.`
  }
  if (tooLarge > 0) {
    return `${tooLarge === 1 ? 'That photograph is' : `${String(tooLarge)} photographs are`} over ${String(MAX_SOURCE_MB)} MB. Choose a smaller copy.`
  }
  return null
}

interface Props {
  /** Called with each dropped or chosen file, in the order they were given. */
  onFiles: (files: File[]) => void
  /** More than one at a time — a room takes several photographs. */
  multiple?: boolean
  busy?: boolean
  disabled?: boolean
  /** What the zone says when it is empty. */
  label: string
  /** A second line, for a limit or a caveat. */
  hint?: string
  className?: string
}

/**
 * Drag a photograph here — or click, or tab to it and press Enter.
 *
 * A drop zone is the fastest way in for somebody with a folder of photographs
 * open, which is the actual situation: nobody photographs a room one file at a
 * time. So dropping is the primary affordance and the zone is large enough to be
 * an obvious target.
 *
 * It is not the *only* affordance, because a drop zone alone cannot be used from a
 * keyboard and cannot be used at all on a phone. The same element is a button and
 * wraps a real file input, so click, tap and Enter all open the picker. Getting
 * this wrong is the usual failure of drag-and-drop uploads — a beautiful zone that
 * a keyboard cannot reach.
 *
 * `dragDepth` is a counter rather than a boolean: dragging over a child element
 * fires `dragleave` on the parent, so a boolean flickers the highlight off while
 * the pointer is still inside the zone.
 */
export function ImageDropzone({
  onFiles,
  multiple = false,
  busy = false,
  disabled = false,
  label,
  hint,
  className,
}: Props): React.ReactElement {
  const inputRef = React.useRef<HTMLInputElement>(null)
  const [dragDepth, setDragDepth] = React.useState(0)
  const [refusal, setRefusal] = React.useState<string | null>(null)
  const over = dragDepth > 0

  function take(list: FileList | null): void {
    if (disabled || !list) return
    const given = Array.from(list)
    const typed = given.filter((f) => ACCEPTED_TYPES.includes(f.type))
    const images = typed.filter((f) => f.size <= MAX_SOURCE_BYTES)
    setRefusal(refusalFor(given.length - typed.length, typed.length - images.length))
    if (images.length === 0) return
    onFiles(multiple ? images : images.slice(0, 1))
  }

  return (
    <>
      <div
        role="button"
        tabIndex={disabled ? -1 : 0}
        aria-disabled={disabled}
        aria-label={label}
        onClick={() => {
          if (!disabled) inputRef.current?.click()
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            if (!disabled) inputRef.current?.click()
          }
        }}
        onDragEnter={(e) => {
          e.preventDefault()
          setDragDepth((d) => d + 1)
        }}
        onDragOver={(e) => {
          // Without this the browser navigates to the file instead of dropping it.
          e.preventDefault()
        }}
        onDragLeave={(e) => {
          e.preventDefault()
          setDragDepth((d) => Math.max(0, d - 1))
        }}
        onDrop={(e) => {
          e.preventDefault()
          setDragDepth(0)
          take(e.dataTransfer.files)
        }}
        className={`flex cursor-pointer flex-col items-center justify-center gap-1.5 rounded-md border-2 border-dashed px-4 py-6 text-center transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus ${
          over ? 'border-primary bg-primary/5' : 'border-line hover:border-ink-muted/50'
        } ${disabled ? 'cursor-not-allowed opacity-60' : ''} ${className ?? ''}`}
      >
        <input
          ref={inputRef}
          type="file"
          accept={ACCEPT}
          multiple={multiple}
          className="hidden"
          disabled={disabled}
          onChange={(e) => {
            take(e.target.files)
            e.target.value = ''
          }}
        />
        {busy ? (
          <Loader2 className="h-5 w-5 animate-spin text-ink-muted" />
        ) : (
          <ImagePlus className="h-5 w-5 text-ink-muted" />
        )}
        <p className="text-sm font-medium text-ink">
          {over ? 'Drop to upload' : label}
        </p>
        {hint ? <p className="text-xs text-ink-muted">{hint}</p> : null}
      </div>
      {refusal ? <FormError size="sm">{refusal}</FormError> : null}
    </>
  )
}
